/**
 * Responsibility: Implement policy behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { SupervisorConfig, IncidentDomain, RemediationPolicy } from "@anyquest/shared-core"

const STORAGE_KEY = 'am2_supervisor_config_v1'
const DOMAINS: IncidentDomain[] = ['actor', 'inventory', 'atlas', 'combat', 'quest', 'effect', 'persistence', 'object']

// Default fail-safe policy: observe everything, auto-repair nothing yet (shadow mode)
export const DEFAULT_SUPERVISOR_CONFIG: SupervisorConfig = {
  enabled: true,
  timeBudgetMs: 1000,
  policies: {
    actor: 'auto',
    inventory: 'auto',
    atlas: 'auto',
    combat: 'auto',
    quest: 'auto',
    effect: 'auto',
    persistence: 'auto',
    object: 'auto',
  }
}

function sanitizePolicy(value: unknown): RemediationPolicy {
  if (value === 'auto' || value === 'confirm' || value === 'observe' || value === 'freeze') return value
  return 'auto'
}

function sanitizeConfig(raw: unknown): SupervisorConfig {
  const src = (raw && typeof raw === 'object') ? raw as Partial<SupervisorConfig> : {}
  const policies = { ...DEFAULT_SUPERVISOR_CONFIG.policies }
  for (const domain of DOMAINS) {
    policies[domain] = sanitizePolicy(src.policies?.[domain])
  }

  return {
    enabled: typeof src.enabled === 'boolean' ? src.enabled : DEFAULT_SUPERVISOR_CONFIG.enabled,
    timeBudgetMs: Number.isFinite(src.timeBudgetMs) ? Math.max(5, Math.min(1000, Number(src.timeBudgetMs))) : DEFAULT_SUPERVISOR_CONFIG.timeBudgetMs,
    policies,
  }
}

function loadPersistedConfig(): SupervisorConfig | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return sanitizeConfig(JSON.parse(raw))
  } catch {
    return null
  }
}

function persistConfig(config: SupervisorConfig) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
  } catch {
    // non-fatal
  }
}

let activeConfig: SupervisorConfig = loadPersistedConfig() ?? structuredClone(DEFAULT_SUPERVISOR_CONFIG)

type SupervisorConfigPatch = Partial<Omit<SupervisorConfig, 'policies'>> & {
  policies?: Partial<Record<IncidentDomain, RemediationPolicy>>
}

export function setSupervisorConfig(config: SupervisorConfigPatch) {
  const merged = {
    ...activeConfig,
    ...config,
    policies: {
      ...activeConfig.policies,
      ...(config.policies ?? {}),
    },
  }
  activeConfig = sanitizeConfig(merged)
  persistConfig(activeConfig)
}

export function resetSupervisorConfig() {
  activeConfig = structuredClone(DEFAULT_SUPERVISOR_CONFIG)
  persistConfig(activeConfig)
}

export function setSupervisorDomainPolicy(domain: IncidentDomain, policy: RemediationPolicy) {
  setSupervisorConfig({
    policies: {
      [domain]: policy,
    },
  })
}

export function getSupervisorConfig(): SupervisorConfig {
  return activeConfig
}

export function getPolicyForDomain(domain: IncidentDomain): RemediationPolicy {
  return activeConfig.policies[domain] ?? 'observe'
}
